/**
 * The creation wizard's Main Info request for a portfolio created in quantities (MDOT-25396): the
 * `quantityMode` choice and the AUM Core sizes the whole quantities to. Both fields apply only to a
 * creation draft, so only the wizard's `create` and `draft` saves pass through here.
 */
import { findAumSystemAttribute } from "$root/functional-areas/customAttributes/aum";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";
import type { CustomAttributeDto } from "$root/functional-areas/customAttributes/type";
import type { CustomAttributeValue, MainInfoSaveRequest } from "@eu.mdotm.api/sphere";

/** The Main Info step values the creation request reads: the mode and the attribute holding the AUM. */
export type CreationModeValues = {
	quantityMode: boolean;
	customAttributes?: Array<CustomAttributeValue | undefined>;
};

/**
 * `request` with the creation mode added. In quantities it also carries the top-level `aum`, read off
 * the AUM system attribute's entry in `values.customAttributes`; that entry still travels in
 * `customAttributes` too. `aum` is omitted in weights, and in quantities when the form holds no AUM.
 */
export function toCreationMainInfoRequest(
	request: MainInfoSaveRequest,
	values: CreationModeValues,
	availableAttributes: CustomAttributeDto[] | undefined,
): MainInfoSaveRequest {
	if (!values.quantityMode) {
		return { ...request, quantityMode: false };
	}
	const aumAttributeUuid = findAumSystemAttribute(availableAttributes)?.attributeUuid;
	const aum =
		aumAttributeUuid == null
			? undefined
			: values.customAttributes?.find((value) => value?.attributeUuid === aumAttributeUuid)?.numberValue;
	return aum == null ? { ...request, quantityMode: true } : { ...request, quantityMode: true, aum };
}

/**
 * {@link toCreationMainInfoRequest} with the tenant's custom attributes fetched for it. A request in
 * weights needs none of them, so it is built without a fetch.
 */
export async function resolveCreationMainInfoRequest(
	request: MainInfoSaveRequest,
	values: CreationModeValues,
): Promise<MainInfoSaveRequest> {
	if (!values.quantityMode) {
		return toCreationMainInfoRequest(request, values, undefined);
	}
	const availableAttributes = await investmentCustomAttributesRepository.customAttributes.fetchQuery();
	return toCreationMainInfoRequest(request, values, availableAttributes);
}
